// Planner-driven answer layout (busy-ai-srv#25): the Places block and the plan a web answer was
// built with. Pure functions, shared by the research store (stream, `done`, one-shot body) and the
// workspace that draws the block — so the three transports cannot produce three different shapes.

// Google requires an attribution line beside any content grounded on Google Maps.
export const PLACES_ATTRIBUTION = 'Google Maps'

// Only a link the browser can open safely: Google gives Maps urls, never anything else, but a
// place is drawn as an <a href>, and a `javascript:` value there would be a hole.
const isWebUrl = (u) => typeof u === 'string' && /^https?:\/\//i.test(u.trim())

// A place → the item the block draws: title + the Google Maps link as Google gave it (no tracking
// added). Read under both spellings the wire may use; places with no usable link are dropped.
export const toPlaceItems = (places, turnId) => (Array.isArray(places) ? places : [])
  .filter(p => p && isWebUrl(p.uri ?? p.Uri))
  .map((p, i) => {
    const url = (p.uri ?? p.Uri).trim()
    const placeId = p.placeId ?? p.PlaceId
    // Where it is (busy-ai-srv#36, OpenStreetMap's Nominatim) — only a real pair of coordinates;
    // a place without them still gets its card, just no pin on the map.
    const lat = coord(p.lat ?? p.Lat, 90)
    const lon = coord(p.lon ?? p.Lon, 180)
    return {
      id: `${turnId}-place-${i}`, kind: 'place', title: (p.title ?? p.Title) || url, url,
      ...(placeId ? { placeId } : {}),
      ...(lat !== null && lon !== null ? { lat, lon } : {}),
    }
  })

// A latitude/longitude inside its range, else null (a string, a null, 200°).
const coord = (v, limit) => (typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= limit ? v : null)

/** Do any of these places have coordinates — is there a map to draw? */
export const hasCoordinates = (places) => (Array.isArray(places) ? places : [])
  .some(p => p && Number.isFinite(p.lat) && Number.isFinite(p.lon))

// The plan ai-srv built the answer with: `{ blocks, freshness, source }`, or null when the answer
// came without one (an older ai-srv, a cached answer, a chat turn). Debugging / later UI only —
// what is drawn is still decided by what actually arrived.
export const answerPlan = (plan) => {
  if (!plan || typeof plan !== 'object') return null
  const blocks = plan.blocks ?? plan.Blocks
  return {
    blocks: Array.isArray(blocks) ? blocks.filter(b => typeof b === 'string') : [],
    freshness: (plan.freshness ?? plan.Freshness) || null,
    source: (plan.source ?? plan.Source) || '',
  }
}

// Is a section still on its way? While the turn works and the section is empty its placeholder
// shows — unless the server already SAID it is empty: since busy-ai-srv#25 an unplanned panel's
// event arrives as an empty list, and a shimmer waiting on it would sit there to the end of the
// answer. `settled` holds the sections whose event has been heard.
export const panelPending = (turn, part) =>
  !!turn?.loading && !(turn?.[part] || []).length && !turn?.settled?.[part]
